// openEHR AM model — declarations with no BMM package in the map
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { ARCHETYPE } from "./aom2/archetype.ts";
import { C_ATTRIBUTE, C_COMPLEX_OBJECT, C_DEFINED_OBJECT, C_REFERENCE_OBJECT } from "./aom2/constraint_model.ts";
import type { C_OBJECT } from "./aom2/constraint_model.ts";
import type { ARCHETYPE_TERM } from "./aom2/terminology.ts";
import * as openehr_base from "../base/mod.ts";

/**
 * Parent of types representing constraints on primitive types.
 */
export abstract class C_PRIMITIVE {
  /**
   * Value to be assumed if none sent in data.
   */
  assumed_value?: openehr_base.Any;
  /**
   * Generate a default value from this constraint object.
   * @returns Result value
   */
  compute_default_value(): openehr_base.Any {
    if (this.assumed_value !== undefined) {
      return this.assumed_value;
    }
    throw new Error("No assumed value for compute_default_value.");
  }

  has_assumed_value(): openehr_base.Boolean {
    return openehr_base.Boolean.from(this.assumed_value !== undefined);
  }

  valid_value(_a_value: openehr_base.Any): openehr_base.Boolean {
    return openehr_base.Boolean.from(true);
  }
}

/**
 * Abstract parent of all expression tree items.
 */
export abstract class EXPR_ITEM {
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * Type name of this item in the mathematical sense. For leaf nodes, must be the name of a primitive type, or else a reference model type. The type for any relational or boolean operator will be “Boolean”, while the type for any arithmetic operator, will be “Real” or “Integer”.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }
}

/**
 * Expression tree leaf item representing one of:
 *
 * * a manifest constant of any primitive type;
 * * a path referring to a value in the archetype;
 * * a constraint;
 * * a variable reference.
 */
export class EXPR_LEAF extends EXPR_ITEM {
  /**
   * Internal storage for reference_type
   * @protected
   */
  protected _reference_type?: openehr_base.String;

  /**
   * Type of reference: “constant”, “attribute”, “function”, “constraint”. The first three are used to indicate the referencing mechanism for an operand. The last is used to indicate a constraint operand, as happens in the case of the right-hand operand of the ‘matches’ operator.
   */
  get reference_type(): string | undefined {
    return this._reference_type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for reference_type.
   * Use this to access openehr_base.String methods.
   */
  get $reference_type(): openehr_base.String | undefined {
    return this._reference_type;
  }

  /**
   * Sets reference_type from either a primitive value or openehr_base.String wrapper.
   */
  set reference_type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._reference_type = undefined;
    } else if (typeof val === "string") {
      this._reference_type = openehr_base.String.from(val);
    } else {
      this._reference_type = val;
    }
  }

  /**
   * The value referred to; a manifest constant, an attribute path (in the form of a String), or for the right-hand side of a ‘matches’ node, a constraint, often a C_PRIMITIVE_OBJECT.
   */
  item?: openehr_base.Any;
}

/**
 * Abstract parent type of domain-specific constrainer types, to be defined in external packages.
 */
export abstract class C_DOMAIN_TYPE extends C_DEFINED_OBJECT {
  /**
   * Standard (i.e. C_OBJECT) form of constraint.
   * @returns Result value
   */
  standard_equivalent(): C_COMPLEX_OBJECT {
    return new C_COMPLEX_OBJECT();
  }
}

/**
 * Constrainer class for Ordinal data.
 */
export class C_ORDINAL extends C_DOMAIN_TYPE {
  /**
   * Value set of allowed Ordinals in the constraint.
   */
  list?: undefined;
}

/**
 * Constrainer class for Scale data (DV_SCALE).
 */
export class C_SCALE extends C_DOMAIN_TYPE {
  /**
   * Value set of allowed scale values in the constraint.
   */
  list?: undefined;
}

/**
 * Constrainer class for Coded text data.
 */
export class C_CODED_TEXT extends C_DOMAIN_TYPE {
  /**
   * Internal storage for terminology
   * @protected
   */
  protected _terminology?: openehr_base.String;

  /**
   * Terminology identifier.
   */
  get terminology(): string | undefined {
    return this._terminology?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for terminology.
   * Use this to access openehr_base.String methods.
   */
  get $terminology(): openehr_base.String | undefined {
    return this._terminology;
  }

  /**
   * Sets terminology from either a primitive value or openehr_base.String wrapper.
   */
  set terminology(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._terminology = undefined;
    } else if (typeof val === "string") {
      this._terminology = openehr_base.String.from(val);
    } else {
      this._terminology = val;
    }
  }

  /**
   * Optional list of codes from the terminology. No list means any code from the terminology is allowed.
   */
  code_list?: undefined;
  /**
   * Internal storage for reference
   * @protected
   */
  protected _reference?: openehr_base.String;

  get reference(): string | undefined {
    return this._reference?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for reference.
   * Use this to access openehr_base.String methods.
   */
  get $reference(): openehr_base.String | undefined {
    return this._reference;
  }

  /**
   * Sets reference from either a primitive value or openehr_base.String wrapper.
   */
  set reference(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._reference = undefined;
    } else if (typeof val === "string") {
      this._reference = openehr_base.String.from(val);
    } else {
      this._reference = val;
    }
  }
}

/**
 * Constrainer class for Quantity data.
 */
export class C_QUANTITY extends C_DOMAIN_TYPE {
  /**
   * Internal storage for property
   * @protected
   */
  protected _property?: openehr_base.String;

  /**
   * Name of physical property for Quantities being constrained.
   */
  get property(): string | undefined {
    return this._property?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for property.
   * Use this to access openehr_base.String methods.
   */
  get $property(): openehr_base.String | undefined {
    return this._property;
  }

  /**
   * Sets property from either a primitive value or openehr_base.String wrapper.
   */
  set property(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._property = undefined;
    } else if (typeof val === "string") {
      this._property = openehr_base.String.from(val);
    } else {
      this._property = val;
    }
  }

  /**
   * Value set of allowed individual Quantity item constraints in this Quantity constraint.
   */
  list?: undefined;
}

/**
 * Constrainer class for a single Quantity.
 */
export class C_QUANTITY_ITEM {
  /**
   * Quantity magnitude constraint.
   */
  magnitude?: undefined;
  /**
   * Internal storage for units
   * @protected
   */
  protected _units?: openehr_base.String;

  /**
   * Optional units constraint.
   */
  get units(): string | undefined {
    return this._units?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for units.
   * Use this to access openehr_base.String methods.
   */
  get $units(): openehr_base.String | undefined {
    return this._units;
  }

  /**
   * Sets units from either a primitive value or openehr_base.String wrapper.
   */
  set units(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._units = undefined;
    } else if (typeof val === "string") {
      this._units = openehr_base.String.from(val);
    } else {
      this._units = val;
    }
  }
}

/**
 * Constrainer object representing a single Ordinal value.
 */
export class ORDINAL {
  /**
   * Terminology code providing the Ordinal's symbol.
   */
  symbol?: openehr_base.CODE_PHRASE;
  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.Integer;

  /**
   * Ordinal value.
   */
  get value(): number | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for value.
   * Use this to access openehr_base.Integer methods.
   */
  get $value(): openehr_base.Integer | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.Integer wrapper.
   */
  set value(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "number") {
      this._value = openehr_base.Integer.from(val);
    } else {
      this._value = val;
    }
  }
}

/**
 * Structural model of a typed first order predicate logic assertion, in the form of an expression tree, including optional variable definitions.
 */
export class ASSERTION {
  /**
   * Internal storage for tag
   * @protected
   */
  protected _tag?: openehr_base.String;

  /**
   * Expression tag, used for differentiating multiple assertions.
   */
  get tag(): string | undefined {
    return this._tag?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for tag.
   * Use this to access openehr_base.String methods.
   */
  get $tag(): openehr_base.String | undefined {
    return this._tag;
  }

  /**
   * Sets tag from either a primitive value or openehr_base.String wrapper.
   */
  set tag(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._tag = undefined;
    } else if (typeof val === "string") {
      this._tag = openehr_base.String.from(val);
    } else {
      this._tag = val;
    }
  }

  /**
   * Internal storage for string_expression
   * @protected
   */
  protected _string_expression?: openehr_base.String;

  /**
   * String form of expression, in case an expression evaluator taking String expressions is used for evaluation.
   */
  get string_expression(): string | undefined {
    return this._string_expression?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for string_expression.
   * Use this to access openehr_base.String methods.
   */
  get $string_expression(): openehr_base.String | undefined {
    return this._string_expression;
  }

  /**
   * Sets string_expression from either a primitive value or openehr_base.String wrapper.
   */
  set string_expression(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._string_expression = undefined;
    } else if (typeof val === "string") {
      this._string_expression = openehr_base.String.from(val);
    } else {
      this._string_expression = val;
    }
  }

  /**
   * Root of expression tree.
   */
  expression?: EXPR_ITEM;
  /**
   * Definitions of variables used in the assertion expression.
   */
  variables?: ASSERTION_VARIABLE[];
}

/**
 * Definition of a named variable used in an assertion expression.
 */
export class ASSERTION_VARIABLE {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of variable.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Internal storage for definition
   * @protected
   */
  protected _definition?: openehr_base.String;

  /**
   * Formal definition of the variable.
   */
  get definition(): string | undefined {
    return this._definition?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for definition.
   * Use this to access openehr_base.String methods.
   */
  get $definition(): openehr_base.String | undefined {
    return this._definition;
  }

  /**
   * Sets definition from either a primitive value or openehr_base.String wrapper.
   */
  set definition(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._definition = undefined;
    } else if (typeof val === "string") {
      this._definition = openehr_base.String.from(val);
    } else {
      this._definition = val;
    }
  }
}

/**
 * Abstract parent of operator types.
 */
export abstract class EXPR_OPERATOR extends EXPR_ITEM {
  /**
   * Internal storage for precedence_overridden
   * @protected
   */
  protected _precedence_overridden?: openehr_base.Boolean;

  /**
   * True if the natural precedence of operators is overridden in the expression represented by this node of the expression tree. If True, parentheses should be introduced around the totality of the syntax expression corresponding to this operator node and its operands.
   */
  get precedence_overridden(): boolean | undefined {
    return this._precedence_overridden?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for precedence_overridden.
   * Use this to access openehr_base.Boolean methods.
   */
  get $precedence_overridden(): openehr_base.Boolean | undefined {
    return this._precedence_overridden;
  }

  /**
   * Sets precedence_overridden from either a primitive value or openehr_base.Boolean wrapper.
   */
  set precedence_overridden(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._precedence_overridden = undefined;
    } else if (typeof val === "boolean") {
      this._precedence_overridden = openehr_base.Boolean.from(val);
    } else {
      this._precedence_overridden = val;
    }
  }

  /**
   * Code of operator.
   */
  operator?: OPERATOR_KIND;
}

/**
 * Unary operator expression node.
 */
export class EXPR_UNARY_OPERATOR extends EXPR_OPERATOR {
  /**
   * Operand node.
   */
  operand?: EXPR_ITEM;
}

/**
 * Binary operator expression node.
 */
export class EXPR_BINARY_OPERATOR extends EXPR_OPERATOR {
  /**
   * Left operand node.
   */
  left_operand?: EXPR_ITEM;
  /**
   * Right operand node.
   */
  right_operand?: EXPR_ITEM;
}

/**
 * Enumeration type for operator types in assertion expressions.
 */
export class OPERATOR_KIND extends openehr_base.String {
}

/**
 * Express constraints on the cardinality of container objects which are the values of multiply-valued attributes, including uniqueness and ordering, providing the means to state that a container acts like a logical list, set or bag. The cardinality cannot contradict the cardinality of the corresponding attribute within the relevant reference model.
 */
export class CARDINALITY {
  /**
   * The interval of this cardinality.
   */
  interval?: openehr_base.Multiplicity_interval;
  /**
   * Internal storage for is_ordered
   * @protected
   */
  protected _is_ordered?: openehr_base.Boolean;

  /**
   * True if the members of the container attribute to which this cardinality refers are ordered.
   */
  get is_ordered(): boolean | undefined {
    return this._is_ordered?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_ordered.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_ordered(): openehr_base.Boolean | undefined {
    return this._is_ordered;
  }

  /**
   * Sets is_ordered from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_ordered(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_ordered = undefined;
    } else if (typeof val === "boolean") {
      this._is_ordered = openehr_base.Boolean.from(val);
    } else {
      this._is_ordered = val;
    }
  }

  /**
   * Internal storage for is_unique
   * @protected
   */
  protected _is_unique?: openehr_base.Boolean;

  /**
   * True if the members of the container attribute to which this cardinality refers are unique.
   */
  get is_unique(): boolean | undefined {
    return this._is_unique?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_unique.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_unique(): openehr_base.Boolean | undefined {
    return this._is_unique;
  }

  /**
   * Sets is_unique from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_unique(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_unique = undefined;
    } else if (typeof val === "boolean") {
      this._is_unique = openehr_base.Boolean.from(val);
    } else {
      this._is_unique = val;
    }
  }

  /**
   * True if the semantics of this cardinality represent a bag, i.e. unordered, non-unique membership.
   * @returns Result value
   */
  is_bag(): openehr_base.Boolean {
    // TODO: Implement is_bag behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method is_bag not yet implemented.");
  }

  /**
   * True if the semantics of this cardinality represent a list, i.e. ordered, non-unique membership.
   * @returns Result value
   */
  is_list(): openehr_base.Boolean {
    // TODO: Implement is_list behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method is_list not yet implemented.");
  }

  /**
   * True if the semantics of this cardinality represent a bag, i.e. unordered, non-unique membership.
   * @returns Result value
   */
  is_set(): openehr_base.Boolean {
    // TODO: Implement is_set behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method is_set not yet implemented.");
  }
}

/**
 * A constraint defined by proxy, using a reference to an object constraint defined elsewhere in the same
 * archetype.
 *
 * Note that since this object refers to another node, there are two objects with available occurrences values. The local occurrences value on an ARCHETYPE_INTERNAL_REF should always be used; when setting this from a serialised form, if no occurrences is mentioned, the target occurrences should be used (not the standard default of {1..1}); otherwise the locally specified occurrences should be used as normal. When serialising out, if the occurrences is the same as that of the target, it can be left out.
 */
export class ARCHETYPE_INTERNAL_REF extends C_REFERENCE_OBJECT {
  /**
   * Internal storage for target_path
   * @protected
   */
  protected _target_path?: openehr_base.String;

  /**
   * Reference to an object node using archetype path notation.
   */
  get target_path(): string | undefined {
    return this._target_path?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for target_path.
   * Use this to access openehr_base.String methods.
   */
  get $target_path(): openehr_base.String | undefined {
    return this._target_path;
  }

  /**
   * Sets target_path from either a primitive value or openehr_base.String wrapper.
   */
  set target_path(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._target_path = undefined;
    } else if (typeof val === "string") {
      this._target_path = openehr_base.String.from(val);
    } else {
      this._target_path = val;
    }
  }
}

/**
 * Concrete model of constraint on a single-valued attribute node. The meaning of the inherited children attribute is that they are alternatives.
 */
export class C_SINGLE_ATTRIBUTE extends C_ATTRIBUTE {
  /**
   * List of alternative constraints for the single child of this attribute within the data.
   * @returns Result value
   */
  alternatives(): C_OBJECT {
    // TODO: Implement alternatives behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method alternatives not yet implemented.");
  }
}

/**
 * Reference to a constraint described in the same archetype, but outside the main constraint structure. This is used to refer to constraints expressed in terms of external resources, such as constraints on terminology value sets.
 */
export class CONSTRAINT_REF extends C_REFERENCE_OBJECT {
  /**
   * Internal storage for reference
   * @protected
   */
  protected _reference?: openehr_base.String;

  /**
   * Reference to a constraint in the archetype local ontology.
   */
  get reference(): string | undefined {
    return this._reference?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for reference.
   * Use this to access openehr_base.String methods.
   */
  get $reference(): openehr_base.String | undefined {
    return this._reference;
  }

  /**
   * Sets reference from either a primitive value or openehr_base.String wrapper.
   */
  set reference(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._reference = undefined;
    } else if (typeof val === "string") {
      this._reference = openehr_base.String.from(val);
    } else {
      this._reference = val;
    }
  }
}

/**
 * Concrete model of constraint on multiply-valued (ie. container) attribute node.
 */
export class C_MULTIPLE_ATTRIBUTE extends C_ATTRIBUTE {
  /**
   * Cardinality of this attribute constraint, if it constraints a container attribute.
   */
  cardinality?: CARDINALITY;
  /**
   * List of constraints representing members of the container value of this attribute within the data. Semantics of the uniqueness and ordering of items in the container are given by the cardinality.
   * @returns Result value
   */
  members(): C_OBJECT {
    // TODO: Implement members behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method members not yet implemented.");
  }
}

/**
 * Local ontology of an archetype.
 */
export class ARCHETYPE_ONTOLOGY {
  /**
   * Directory of term definitions as a two-level table. The outer hash keys
   * are language codes, e.g. `"en"`, while the inner hash keys are term codes,
   * e.g. `"at0004"`, mapping to term attribute records (text, description, …).
   */
  term_definitions?: Record<
    string,
    Record<string, { text?: string; description?: string }>
  >;
  /**
   * Directory of bindings to external terminology codes, keyed by terminology
   * id then term code.
   */
  term_bindings?: Record<string, Record<string, unknown>>;
  /**
   * Directory of constraint bindings, keyed by terminology id then constraint
   * code.
   */
  constraint_bindings?: Record<string, Record<string, unknown>>;
  /**
   * Directory of locally defined value sets, keyed by value-set (ac) code.
   */
  value_sets?: Record<string, unknown>;
  /**
   * List of all term codes in the ontology. Most of these correspond to “at” codes in an ADL archetype, which are the node_ids on C_OBJECT descendants. There may be an extra one, if a different term is used as the overall archetype concept from that used as the node_id of the outermost C_OBJECT in the definition part.
   */
  term_codes?: undefined;
  /**
   * List of all term codes in the ontology. These correspond to the “ac” codes in an ADL archetype, or equivalently, the CONSTRAINT_REF.reference values in the archetype definition.
   */
  constraint_codes?: undefined;
  /**
   * Archetype which owns this terminology.
   */
  parent_archetype?: ARCHETYPE;
  /**
   * List of terminologies to which term or constraint bindings exist in this terminology.
   */
  terminologies_available?: undefined;
  /**
   * Internal storage for specialisation_depth
   * @protected
   */
  protected _specialisation_depth?: openehr_base.Integer;

  /**
   * Specialisation depth of this archetype. Unspecialised archetypes have depth 0, with each additional level of specialisation adding 1 to the specialisation_depth.
   */
  get specialisation_depth(): number | undefined {
    return this._specialisation_depth?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for specialisation_depth.
   * Use this to access openehr_base.Integer methods.
   */
  get $specialisation_depth(): openehr_base.Integer | undefined {
    return this._specialisation_depth;
  }

  /**
   * Sets specialisation_depth from either a primitive value or openehr_base.Integer wrapper.
   */
  set specialisation_depth(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._specialisation_depth = undefined;
    } else if (typeof val === "number") {
      this._specialisation_depth = openehr_base.Integer.from(val);
    } else {
      this._specialisation_depth = val;
    }
  }

  term_attribute_names?: undefined;
  /**
   * True if terminology ‘a_terminology’ is present in archetype ontology.
   * @param a_lang - Parameter
   * @returns Result value
   */
  has_language(a_lang: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_language behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_language not yet implemented.");
  }

  /**
   * True if terminology \`a_terminology' is present in archetype ontology.
   * @param a_terminology_id - Parameter
   * @returns Result value
   */
  has_terminology(a_terminology_id: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_terminology behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_terminology not yet implemented.");
  }

  /**
   * True if term_codes has a_code.
   * @param a_code - Parameter
   * @returns Result value
   */
  has_term_code(a_code: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_term_code behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_term_code not yet implemented.");
  }

  /**
   * True if constraint_codes has a_code.
   * @param a_code - Parameter
   * @returns Result value
   */
  has_constraint_code(a_code: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_constraint_code behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_constraint_code not yet implemented.");
  }

  /**
   * Term definition for a code, in a specified language.
   * @param a_lang - Parameter
   * @param a_code - Parameter
   * @returns Result value
   */
  term_definition(
    a_lang: openehr_base.String,
    a_code: openehr_base.String,
  ): ARCHETYPE_TERM {
    // TODO: Implement term_definition behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method term_definition not yet implemented.");
  }

  /**
   * Constraint definition for a code, in a specified language.
   * @param a_code - Parameter
   * @param a_lang - Parameter
   * @returns Result value
   */
  constraint_definition(
    a_code: openehr_base.String,
    a_lang: openehr_base.String,
  ): ARCHETYPE_TERM {
    // TODO: Implement constraint_definition behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method constraint_definition not yet implemented.");
  }

  /**
   * Binding of constraint corresponding to a_code in target external terminology a_terminology_id, as a string, which is usually a formal query expression.
   * @param a_terminology - Parameter
   * @param a_code - Parameter
   * @returns Result value
   */
  term_binding(
    a_terminology: openehr_base.String,
    a_code: openehr_base.String,
  ): openehr_base.CODE_PHRASE {
    // TODO: Implement term_binding behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method term_binding not yet implemented.");
  }

  /**
   * Binding of constraint corresponding to a_code in target external terminology a_terminology_id, as a string, which is usually a formal query expression.
   * @param a_terminology_id - Parameter
   * @param a_code - Parameter
   * @returns Result value
   */
  constraint_binding(
    a_terminology_id: openehr_base.String,
    a_code: openehr_base.String,
  ): openehr_base.String {
    // TODO: Implement constraint_binding behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method constraint_binding not yet implemented.");
  }
}
